import { useRouter } from "next/router";
import { ReactNode } from "react";

import BreadCrumb from "shared/BreadCrumb";
import { AppLayout } from "shared/Layout";
import { fetchSettings } from "shared/SettingsProvider/settings.helper";
import { AvailableCatalog } from "shared/catalog/AvailableCatalog";
import { CatalogBannerAvailable } from "shared/catalog/AvailableCatalog/CatalogBannerAvailable";
import {
  getAvailableCatalogTags,
  getAvailableEntries,
} from "shared/catalog/AvailableCatalog/available-catalog.helper";
import { BestSellersCatalog } from "shared/catalog/BestSellersCatalog";
import { BestSellersContextProvider } from "shared/catalog/BestSellersCatalog/BestSellersContext";
import { getBestSellerEntries } from "shared/catalog/BestSellersCatalog/best-sellers-catalog.helper";
import { CatalogCategories } from "shared/catalog/CatalogCategories";
import { CatalogFilters } from "shared/catalog/CatalogFilters";
import { CatalogHeader } from "shared/catalog/CatalogHeader";
import { CatalogPageContextProvider } from "shared/catalog/CatalogPageContext";
import { CatalogSorting } from "shared/catalog/CatalogSorting";
import { CatalogStickySection } from "shared/catalog/CatalogStickySection";
import { ComingSoonCatalog } from "shared/catalog/ComingSoonCatalog";
import { CatalogBannerComingSoon } from "shared/catalog/ComingSoonCatalog/CatalogBannerComingSoon";
import { getComingSoonEntries } from "shared/catalog/ComingSoonCatalog/coming-soon-catalog.helper";
import { LegacyCatalog } from "shared/catalog/LegacyCatalog";
import { CatalogBannerLegacy } from "shared/catalog/LegacyCatalog/CatalogBannerLegacy";
import { getLegacyEntries } from "shared/catalog/LegacyCatalog/legacy-catalog.helper";
import { CACHE_ONE_HOUR } from "utils/config.utils";
import { withApp } from "utils/with-app.utils";
import { fetchTranslations, useI18n } from "utils/with-i18n.utils";
import { fetchSEO, withSeo } from "utils/with-seo.utils";

export type SmartphonesProps = Awaited<
  ReturnType<typeof getStaticProps>
>["props"];

export const Smartphones = ({
  catalogData,
  bestSellerEntries,
}: SmartphonesProps) => {
  const i18n = useI18n();
  const { query } = useRouter();

  return (
    <CatalogPageContextProvider catalogData={catalogData}>
      <div className="flex flex-col gap-5 m-5 max-w-7xl sm:gap-7 sm:m-7 xl:mx-auto">
        <BreadCrumb
          className="m-0"
          parts={
            query.slug
              ? [
                  { label: i18n.t("breadcrumb.home"), to: "/" },
                  {
                    label: i18n.t("breadcrumb.catalog"),
                    to: "/smartphone",
                  },
                  {
                    label: i18n.t(`breadcrumb.category.${query.slug}`),
                    to: `/smartphone/${query.slug}`,
                  },
                ]
              : [
                  { label: i18n.t("breadcrumb.home"), to: "/" },
                  {
                    label: i18n.t("breadcrumb.catalog"),
                    to: "/smartphone",
                  },
                ]
          }
        />

        {/* Header */}
        <CatalogHeader />

        {/* Legacy page content */}
        {query.slug === "legacy" ? (
          <LegacyCatalog />
        ) : query.slug === "prochainement" ? (
          <ComingSoonCatalog />
        ) : query.slug === "meilleures-ventes" ? (
          <BestSellersContextProvider entries={bestSellerEntries}>
            <BestSellersCatalog />
          </BestSellersContextProvider>
        ) : (
          <>
            {/* Not legacy page content */}
            {/* Banner */}
            <CatalogBannerAvailable />

            {/* Filters and products */}
            <div className="flex gap-7">
              {/* Filters */}
              <aside className="hidden w-60 lg:block">
                <CatalogFilters />
              </aside>

              {/* Products */}
              <div className="flex flex-col flex-1 gap-7">
                {/* Sorting */}
                <div className="flex gap-5 justify-between">
                  <CatalogCategories />
                  <div className="hidden lg:flex">
                    <CatalogSorting />
                  </div>
                </div>

                {/* Available products */}
                <AvailableCatalog layoutHasFilters={true} />

                {/* Coming soon title */}
                <CatalogBannerComingSoon />

                {/* Coming soon products */}
                <ComingSoonCatalog layoutHasFilters={true} />

                {/* Sticky section */}
                <div className="sticky bottom-0 z-50 -mx-5 lg:hidden">
                  <CatalogStickySection />
                </div>

                {/* Legacy banner */}
                <CatalogBannerLegacy />
              </div>
            </div>
          </>
        )}
      </div>
    </CatalogPageContextProvider>
  );
};

Smartphones.getLayout = (page: ReactNode, pageProps: SmartphonesProps) => (
  <AppLayout withBanner {...pageProps}>
    {page}
  </AppLayout>
);

type GetStaticPropsParams = {
  params?: { slug?: string };
};

export const getStaticProps = async ({ params }: GetStaticPropsParams) => {
  const translations = await fetchTranslations([
    "catalog",
    "marquee",
    "product",
    "breadcrumb",
  ]);

  const [
    tags,
    availableEntries,
    comingSoonEntries,
    legacyEntries,
    bestSellerEntries,
  ] = await Promise.all([
    getAvailableCatalogTags(),
    getAvailableEntries(),
    getComingSoonEntries(),
    getLegacyEntries(),
    getBestSellerEntries(),
  ]);

  const seo = await fetchSEO(
    params?.slug ? `tag/${params.slug}` : "smartphone",
  );

  return {
    props: {
      catalogData: {
        tags,
        availableEntries,
        comingSoonEntries,
        legacyEntries,
      },
      translations,
      seo,
      settings: await fetchSettings(),
      bestSellerEntries,
    },
    revalidate: CACHE_ONE_HOUR,
  };
};

export default withApp(Smartphones, [withSeo]);
